import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { GGOActions, GGOErrorType, GGOState } from '@lib/services/api/types'
import { useApiService, useGnarContainerId, useUserService } from '@lib/hooks'
import { PageContext } from '@contexts/page-context'
import { useSyncBackendPromptCapToLocalStorage } from '@components/section/go-templates-tool/local-storage-state'
import {
  CheetahChunkMessage,
  CheetahErrorMessage,
  CheetahResultMessage,
  CheetahSetContextMessage,
  WSError,
} from '@lib/services/api/cheetah-utils'
import { gnarSpec } from '@lib/tracking/gnar'
import { Document } from '@contentful/rich-text-types'
import { ParaphrasingPromptType } from '@components/section/go-templates-tool/utils'
import { fillOutLlmPrompt } from '@components/section/go-templates-tool/form/fill-out-llm-prompt'
import { CapiWsClient, CheetahResultState } from '@lib/services/api/cheetah-client'
import { getLogger } from '@lib/tracking/felog/telemetry'
import { UrlBasedUsageTracker } from '@lib/utils/tool-usage'
import { isSampleTextSubmission, parseSingleFieldFormData } from '@components/tool/utils'

const ggoStateContext = React.createContext<GGOState>({
  cheetahResultState: 'initial',
})
export const GgoStateContextProvider = ggoStateContext.Provider
export const useGgoState = () => useContext(ggoStateContext)

const ggoResultContext = React.createContext('')
export const GgoResultContextProvider = ggoResultContext.Provider
export const useGgoResult = () => useContext(ggoResultContext)

const ggoActionsContext = React.createContext<GGOActions | undefined>(undefined)
export const GgoActionsProvider = ggoActionsContext.Provider
export const useGgoActions = () => {
  const actions = useContext(ggoActionsContext)
  if (!actions) {
    throw new Error('Tried to access GGO actions but there is no GGO Provider')
  }
  return actions
}

interface CapiWsProviderProps {
  children?: React.ReactNode
  skip?: boolean
  onTriggerWall: () => void
  onCheetahActionSend?: () => void
  usageTracker?: UrlBasedUsageTracker
  sampleText?: string
  toolInputName?: string
}

export const CapiWsProvider = (props: CapiWsProviderProps) => {
  const { children, skip, onTriggerWall, onCheetahActionSend, usageTracker, sampleText, toolInputName } = props
  const { cheetah } = useApiService()
  const { isAnonymous } = useUserService().currentUser
  const { pageId, slug } = useContext(PageContext)
  const { containerId: gnarContainerId, containerIdPromise: gnarContainerIdPromise } = useGnarContainerId()

  const [ggoResult, setGGOResult] = React.useState('')
  /** Used to prevent stale closures in setTimeouts */
  const stateAsRef = useRef({ ggoResult: '', cheetahResultState: 'initial' as CheetahResultState })
  const [ggoError, setGGOError] = React.useState<GGOErrorType | undefined>(undefined)
  const [cheetahResultState, setCheetahResultState] = useState<CheetahResultState>('initial')
  const loadingTimeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([])
  const onSyncBackendPromptCap = useSyncBackendPromptCapToLocalStorage(isAnonymous)
  /** Store the submitted text to check against sample text when result comes back */
  const submittedTextRef = useRef<string>('')

  const clearLoadingTimeoutRefs = useCallback(() => {
    const handles = loadingTimeoutRefs.current
    if (handles) {
      handles.forEach((handle) => {
        if (handle) {
          clearTimeout(handle)
        }
      })
    }
    loadingTimeoutRefs.current = []
  }, [loadingTimeoutRefs])

  const setWsError = useCallback(
    (e: unknown) => {
      clearLoadingTimeoutRefs()

      if (e instanceof WSError) {
        switch (e._promptSignup) {
          case 'popup': {
            getLogger('cms.capi.ws').info('WSError with popup signup triggered, showing wall')
            onTriggerWall()
            setCheetahResultState('error')
            setGGOError({ msg: e._msg, reason: e._reason, kind: e._kind })
            break
          }
          default: {
            getLogger('cms.capi.ws').info('WSError without popup signup, setting error state')
            setCheetahResultState('error')
            setGGOError({ msg: e._msg, reason: e._reason, kind: e._kind })
          }
        }
      } else {
        getLogger('cms.capi.ws').warn('Non-WSError passed to setWsError:', e)
        setCheetahResultState('error')
        setGGOError({ msg: 'Unknown error initiating websocket', reason: e })
      }
    },
    [onTriggerWall, clearLoadingTimeoutRefs]
  )

  const onCheetahResult = useCallback(
    (_e: CheetahResultMessage) => {
      // Only increment usage if not submitting sample text
      const isUsingSampleText = isSampleTextSubmission(submittedTextRef.current, sampleText)
      if (usageTracker && !isUsingSampleText) {
        usageTracker.incrementToolUsageState()
      }
      setCheetahResultState('done')
    },
    [usageTracker, sampleText]
  )

  const onCheetahChunk = useCallback((e: CheetahChunkMessage) => {
    setCheetahResultState('streaming')
    // TODO handle multiple prompt responses coming in on WS
    setGGOResult((r) => r + e.token)
  }, [])

  const onCheetahError = useCallback((_e: CheetahErrorMessage) => {
    // Error handling is done by the client
  }, [])

  const onCheetahSetContext = useCallback((e: CheetahSetContextMessage) => {
    // Context handling is done by the client, but we need to handle the state reset case
    if (e.usage && e.usage.promptsRemaining === 0) {
      setCheetahResultState('initial')
    }
  }, [])

  const capiWsClientRef = useRef<CapiWsClient | null>(null)

  // Initialize the client once
  if (!capiWsClientRef.current) {
    capiWsClientRef.current = new CapiWsClient({
      isAnonymous,
      cheetah,
      gnarContainerId: gnarContainerIdPromise,
      cheetahCallbacks: {
        onCheetahResult,
        onCheetahChunk,
        onCheetahError,
        onCheetahSetContext,
        onError: setWsError,
        onCheetahActionSend,
      },
      callbacks: {
        onSyncBackendPromptCap,
        onTriggerWall,
      },
    })
  }

  useEffect(() => {
    stateAsRef.current = { ggoResult, cheetahResultState }
  }, [ggoResult, cheetahResultState])

  useEffect(() => {
    // Removed the isAnonymous check because we want to initiate the WS connection for anonymous users too
    if (!skip && gnarContainerId && capiWsClientRef.current) {
      void capiWsClientRef.current.initiateWSConnection()
    }
  }, [skip, gnarContainerId, isAnonymous])

  const setLoadingTimeout = (time: number) => {
    loadingTimeoutRefs.current.push(
      setTimeout(() => {
        const s = stateAsRef.current
        if (s.cheetahResultState === 'loading' && !s.ggoResult) {
          gnarSpec.goToolsGenerateStillLoading(pageId, slug, time)
        }
      }, time)
    )
  }

  const sendPrompt = async (
    llmPrompt: Document,
    formData: FormData,
    paraphrasingPrompt?: ParaphrasingPromptType
  ): Promise<boolean> => {
    try {
      // Extract and store submitted text for sample text comparison
      if (toolInputName) {
        submittedTextRef.current = parseSingleFieldFormData(toolInputName, formData)
      }

      const prompt = fillOutLlmPrompt(llmPrompt, formData, paraphrasingPrompt)
      getLogger('cms.capi.ws').debug('[SendPrompt] Generated prompt', {
        gnarContainerId,
      })
      setCheetahResultState('loading')
      setGGOResult('')
      setGGOError(undefined)

      clearLoadingTimeoutRefs()
      setLoadingTimeout(5000)
      setLoadingTimeout(10000)
      setLoadingTimeout(20000)
      setLoadingTimeout(30000)

      if (capiWsClientRef.current) {
        return await capiWsClientRef.current.sendPrompt(prompt)
      } else {
        throw new Error('CAPI WS Client not initialized')
      }
    } catch (e) {
      getLogger('cms.capi.ws').error('[SendPrompt] provider.sendPrompt failed with exception:', {
        errorMessage: e instanceof Error ? e.message : String(e),
        errorName: e instanceof Error ? e.name : typeof e,
        stackTrace: e instanceof Error ? e.stack : undefined,
        errorObject: e,
        gnarContainerId,
      })
      setWsError(e)
      return false
    }
  }

  return (
    <GgoStateContextProvider
      value={{
        cheetahResultState,
        error: ggoError,
      }}
    >
      <GgoResultContextProvider value={ggoResult}>
        <GgoActionsProvider value={{ sendPrompt }}>{children}</GgoActionsProvider>
      </GgoResultContextProvider>
    </GgoStateContextProvider>
  )
}
